import React, { useMemo } from 'react'
import { observer } from 'mobx-react'
import ResultsCardTable from '@Components/Tables/ResultsCardTable'
import { GetId } from '@App/interfaces/get-id'
import { QueryPartyStore } from '@App/stores/search/party/query-party-store'

interface PartyCardItem<resultType> extends GetId {
    partyDetails: resultType
    onTitleClick: (partyDetails: resultType) => void
}

interface PartyResultsCardTableProps<resultType> {
    partyQueryStore: QueryPartyStore<resultType, object>
    loading: boolean
    onTitleClick: (partyDetails: resultType) => void
    PartyDetailsCardComponent: React.ComponentType<{
        partyDetails: resultType
        onTitleClick: (partyDetails: resultType) => void
    }>
}

/**
 * The query-driven flavour of `PartyResultsTable`: the same cards, rendered through the generic `ResultsCardTable`
 * so it shows what the Work tab does (an inline preloader, and "No results" before any search).
 */
const PartyResultsCardTable = <resultType,>({
    partyQueryStore,
    loading,
    onTitleClick,
    PartyDetailsCardComponent,
}: PartyResultsCardTableProps<resultType>) => {
    const ExtendedCard = useMemo(() => {
        const Card = (item: PartyCardItem<resultType>) => (
            <PartyDetailsCardComponent partyDetails={item.partyDetails} onTitleClick={item.onTitleClick} />
        )
        return Card
    }, [PartyDetailsCardComponent])

    const items: Array<PartyCardItem<resultType>> = partyQueryStore.searchResults.map((partyDetails, index) => ({
        partyDetails,
        onTitleClick,
        getId: () => index,
    }))

    return (
        <ResultsCardTable
            items={items}
            count={partyQueryStore.totalNumberOfResults}
            paginationInfo={partyQueryStore.searchByCriteriaPaginationInfo}
            limit={partyQueryStore.resultsPerPageLimit}
            page={partyQueryStore.currentPage}
            loading={loading}
            onChangePage={(pagination) => partyQueryStore.setCurrentPage(pagination.page ?? 1)}
            onChangeLimit={(newLimit) => partyQueryStore.setResultsPerPage(newLimit)}
            extendedCardType={ExtendedCard}
            outerDivClassName="party-results-table"
        />
    )
}

export default observer(PartyResultsCardTable)
